/**
 * Google Fonts, loaded on demand.
 *
 * The canvas renderer can only draw a face the document has already loaded,
 * and a missing one falls back silently to Times — which is how a video gets
 * exported in the wrong font with nothing to say so. So every family a
 * composition uses is requested here and `document.fonts.load` is awaited for
 * each weight/style before a frame that needs it is trusted.
 */

export interface VideoFont {
    family: string;
    /** Weights to request. */
    weights: number[];
    italic?: boolean;
    category: VideoFontCategory;
}

export type VideoFontCategory = 'serif' | 'sans' | 'display' | 'script' | 'mono';

export const VIDEO_FONTS: VideoFont[] = [
    { family: 'Cormorant Garamond', weights: [300, 400, 500, 600], italic: true, category: 'serif' },
    { family: 'Playfair Display', weights: [400, 500, 600], italic: true, category: 'serif' },
    { family: 'Bodoni Moda', weights: [400, 500, 600, 700], italic: true, category: 'serif' },
    { family: 'DM Serif Display', weights: [400], italic: true, category: 'serif' },
    { family: 'Libre Baskerville', weights: [400, 700], italic: true, category: 'serif' },
    { family: 'Fraunces', weights: [300, 400, 600], italic: true, category: 'serif' },
    { family: 'Inter', weights: [300, 400, 500, 600, 700, 800], category: 'sans' },
    { family: 'Manrope', weights: [300, 400, 500, 600, 700], category: 'sans' },
    { family: 'Montserrat', weights: [300, 400, 500, 600, 700, 800], category: 'sans' },
    { family: 'Poppins', weights: [300, 400, 500, 600, 700], category: 'sans' },
    { family: 'Syne', weights: [400, 500, 600, 700, 800], category: 'sans' },
    { family: 'Space Grotesk', weights: [300, 400, 500, 700], category: 'sans' },
    { family: 'Archivo Black', weights: [400], category: 'display' },
    { family: 'Bebas Neue', weights: [400], category: 'display' },
    { family: 'Italiana', weights: [400], category: 'display' },
    { family: 'Allura', weights: [400], category: 'script' },
    { family: 'Pinyon Script', weights: [400], category: 'script' },
    { family: 'DM Mono', weights: [300, 400, 500], category: 'mono' },
];

const requested = new Map<string, Promise<void>>();

function cssUrl(font: VideoFont): string {
    const name = font.family.replace(/ /g, '+');
    if (font.italic) {
        const axes = [
            ...font.weights.map((w) => `0,${w}`),
            ...font.weights.map((w) => `1,${w}`),
        ].join(';');
        return `https://fonts.googleapis.com/css2?family=${name}:ital,wght@${axes}&display=swap`;
    }
    return `https://fonts.googleapis.com/css2?family=${name}:wght@${font.weights.join(';')}&display=swap`;
}

function injectStylesheet(href: string): Promise<void> {
    return new Promise((resolve) => {
        if (typeof document === 'undefined') return resolve();
        const existing = document.querySelector(`link[data-ve-font="${href}"]`);
        if (existing) return resolve();
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute('data-ve-font', href);
        link.onload = () => resolve();
        // A font that cannot load is not fatal — the frame falls back.
        link.onerror = () => resolve();
        document.head.appendChild(link);
    });
}

/** Request a family's stylesheet once. Unknown families (e.g. a brand font) are tried as-is. */
export function requestFontFamily(family: string): Promise<void> {
    const key = family.trim();
    if (!key) return Promise.resolve();
    const cached = requested.get(key);
    if (cached) return cached;
    const known = VIDEO_FONTS.find((f) => f.family.toLowerCase() === key.toLowerCase());
    const font: VideoFont = known ?? { family: key, weights: [300, 400, 500, 600, 700], italic: true, category: 'sans' };
    const p = injectStylesheet(cssUrl(font));
    requested.set(key, p);
    return p;
}

export interface FontUse {
    family: string;
    weight: number;
    style?: 'normal' | 'italic';
}

/** Make sure every face is drawable. Resolves even if some fail (with a timeout). */
export async function ensureFonts(uses: FontUse[], timeoutMs = 6000): Promise<void> {
    if (typeof document === 'undefined' || !(document as any).fonts) return;
    const unique = new Map<string, FontUse>();
    for (const u of uses) unique.set(`${u.family}|${u.weight}|${u.style || 'normal'}`, u);
    const families = Array.from(new Set(Array.from(unique.values()).map((u) => u.family)));
    await Promise.all(families.map(requestFontFamily));
    const loads = Array.from(unique.values()).map((u) =>
        (document as any).fonts
            .load(`${u.style === 'italic' ? 'italic ' : ''}${u.weight} 48px "${u.family}"`)
            .catch(() => undefined),
    );
    await Promise.race([
        Promise.all(loads),
        new Promise((r) => setTimeout(r, timeoutMs)),
    ]);
}

export function cssFontStack(family: string): string {
    const known = VIDEO_FONTS.find((f) => f.family === family);
    const fallback = known?.category === 'serif'
        ? 'Georgia, serif'
        : known?.category === 'mono'
        ? 'monospace'
        : known?.category === 'script'
        ? 'cursive'
        : 'system-ui, sans-serif';
    return `"${family}", ${fallback}`;
}
